import React, { useEffect, useState } from 'react'
import moment from 'moment'
import Endpoints from '../../constants/Endpoints'
import { CurrentCompanyStore } from '../../stores/CurrentCompanyStore'
import { CurrentUserStore } from '../../stores/CurrentUserStore'
import { inject } from 'mobx-react'
import { observer } from 'mobx-react-lite'
import { DatePicker, message } from 'antd'
import { BlazeButton } from '../CustomButtons/BlazeButton'
import { IInventoryLog } from '@getgreenline/inventory/build/models/inventoryLog'
import {
  createProcessOnDateLog,
  ICreateHashKeyRequestContract,
  ITenantHashKeyPathParams,
} from '@getgreenline/ocs'
import './ProcessOnDatePicker.scss'
import { GrRegex, parseErrorMsg } from '../../utilities/helpers'
import { InventoryLogModels } from '@getgreenline/inventory'

interface Props {
  currentCompanyStore?: CurrentCompanyStore
  currentUserStore?: CurrentUserStore
  log: IInventoryLog | undefined
}

const ProcessOnDatePickerComponent = ({ currentCompanyStore, currentUserStore, log }: Props) => {
  const [hashKeys, setHashKeys] = useState<ICreateHashKeyRequestContract[]>([])
  const [selectedProcessOnDate, setSelectedProcessOnDate] = useState<Date | undefined>(undefined)
  const [isSale, setIsSale] = useState<boolean>(false)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [open, setOpen] = useState<boolean>(false)

  useEffect(() => {
    setHashKeys(currentCompanyStore?.globalHashKeys || [])
    setLogTypeSale()
  }, [currentCompanyStore?.globalHashKeys])

  const setLogTypeSale = () => {
    if (log?.type === 'sale') {
      setIsSale(true)
    }
  }

  const isComplianceCategoryNull = log?.complianceCategoryId === null

  const disableFutureDates = (current: moment.Moment | null) => {
    if (!current) return false

    const tomorrow = new Date()
    tomorrow.setDate(tomorrow.getDate() + 1)

    const startOfTomorrow = new Date(
      tomorrow.getFullYear(),
      tomorrow.getMonth(),
      tomorrow.getDate(),
    )
    const currentDate = new Date(current.year(), current.month(), current.date())

    return currentDate >= startOfTomorrow
  }

  const updateSelectedProcessOnDate = (date: moment.Moment | null) => {
    if (date === null) {
      setSelectedProcessOnDate(undefined)
      setOpen(false)
    } else {
      const vanillaDate = date.toDate()
      setSelectedProcessOnDate(vanillaDate)
      setOpen(true)
    }
  }

  const formatProcessOnDate = (date: Date) => {
    const year = date.toLocaleString('default', { year: 'numeric' })
    const month = date.toLocaleString('default', { month: '2-digit' })
    const day = date.toLocaleString('default', { day: '2-digit' })
    return year + '-' + month + '-' + day
  }

  // The API Gateway Mapping Templates used for OCS don't accept escaped double quotes
  const transformLog = (log: IInventoryLog): InventoryLogModels.IInventoryLog => {
    return {
      ...log,
      productName: GrRegex.replaceDoubleQuotes(log.productName),
      message: GrRegex.replaceDoubleQuotes(log.message),
      type: log.type as InventoryLogModels.InventoryLogType,
    }
  }

  const submit = async () => {
    try {
      if (!log) {
        throw new Error('Inventory Log is undefined')
      }
      if (!log.productSku || !log.productBarcode) {
        throw new Error('Product SKU or Barcode is missing')
      }
      if (!selectedProcessOnDate) {
        throw new Error('Error: No process on date selected')
      }

      setIsSubmitting(true)

      const currentUserId = currentUserStore?.currentUser?.id
      const entityId = `${log?.entityId}`

      const hashKeyObj = hashKeys.find((obj) => obj.retailerIds.includes(entityId))
      const hashKey = hashKeyObj?.hashKey || ''

      if (!hashKey) {
        throw new Error('No hashkey associated with this store location')
      }

      const tenantHashKeyPathParams: ITenantHashKeyPathParams = {
        gateway: Endpoints.AUTHZ_GATEWAY_URL,
        tenantId: `${log?.companyId}`,
        hashKey,
      }

      const payload = {
        processOnDate: formatProcessOnDate(selectedProcessOnDate),
        updateBy: currentUserId ? `userId-${currentUserId}` : '',
        inventoryLogId: `${log.id}`,
        inventoryLog: transformLog(log),
      }

      await createProcessOnDateLog(tenantHashKeyPathParams, payload)
      message.info('Process on Date was successfully applied')
      setSelectedProcessOnDate(undefined)
      setOpen(false)
    } catch (error) {
      console.error(error)
      message.error(parseErrorMsg(error))
    } finally {
      setIsSubmitting(false)
    }
  }

  const applyProcessOnDateButton = () => (
    <div style={{ textAlign: 'right' }}>
      <BlazeButton
        buttonProps={{
          type: 'primary',
          size: 'small',
          htmlType: 'submit',
          disabled: isSubmitting,
          onClick: submit,
        }}
        titleProps={{
          title: isSubmitting ? 'Applying' : 'Apply',
        }}
      />
    </div>
  )

  return (
    <DatePicker
      className='ocs-process-on-date-picker'
      size='small'
      disabled={isSale || isComplianceCategoryNull}
      open={open}
      disabledDate={disableFutureDates}
      onChange={updateSelectedProcessOnDate}
      onOpenChange={(open) => setOpen(open)}
      renderExtraFooter={() => applyProcessOnDateButton()}
    />
  )
}

export const ProcessOnDatePicker = inject(
  'currentUserStore',
  'currentCompanyStore',
)(observer(ProcessOnDatePickerComponent))
